import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Award, Briefcase, TrendingUp, Users, Target, Lightbulb, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { directorInfo, smartPrinciples } from '../data/mock';

export const About = () => {
  return (
    <section id="about" className="py-20 bg-white">
      <Helmet>
        <title>About In Smart Media | Solapur's Trusted Advertising Agency Since 2009</title>
        <meta name="description" content="Learn about In Smart Media's 17+ year journey in Solapur. Meet our founders Yogesh Wagh (23+ years) and Siddharam Wagh (24+ years). Discover our SMART advertising approach and 500+ successful campaigns." />
        <meta name="keywords" content="about In Smart Media, Yogesh Wagh, Siddharam Wagh, advertising agency Solapur founders, SMART advertising approach, Solapur advertising history" />
        <link rel="canonical" href="https://www.insmartmedia.com/#about" />
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            About <span className="text-blue-600">IN SMART MEDIA</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Transforming businesses through strategic advertising and creative excellence
          </p>
        </div>

        {/* Leadership Section - Both Directors */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              Our <span className="text-blue-600">Leadership</span>
            </h3>
            <p className="text-lg text-gray-600">
              Guided by visionary leaders with decades of media industry expertise
            </p>
          </div>

          {/* Directors Grid */}
          <div className="grid lg:grid-cols-2 gap-12 mb-12">
            {directorInfo.directors.map((director, idx) => (
              <div key={idx} className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all">
                <div className="flex flex-col items-center text-center mb-6">
                  <div className="relative mb-6">
                    <div className="relative z-10 rounded-2xl overflow-hidden shadow-xl w-48 h-48">
                      <img 
                        src={director.image}
                        alt={director.name}
                        width="200"
                        height="200"
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    {/* Experience Badge */}
                    <div className="absolute z-10 -bottom-4 -right-4 bg-blue-600 text-white rounded-xl px-4 py-2 shadow-lg">
                      <div className="text-xl font-bold">{director.experience}</div>
                      <div className="text-xs">Experience</div>
                    </div>
                  </div>
                  
                  <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-600 px-4 py-2 rounded-full mb-3">
                    <Award size={20} />
                    <span className="font-semibold">{director.title}</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-1">{director.name}</h3>
                  <p className="text-lg text-blue-600 font-semibold mb-4">{director.designation}</p>
                </div>

                <p className="text-gray-600 leading-relaxed mb-6 text-center">
                  {director.description}
                </p>
                
                {director.additionalInfo && (
                  <p className="text-gray-600 leading-relaxed mb-6 text-center text-sm">
                    {director.additionalInfo}
                  </p>
                )}

                {/* Expertise Grid */}
                <div className="grid grid-cols-2 gap-3">
                  {director.expertise.map((skill, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <CheckCircle2 className="text-blue-600 mt-1 flex-shrink-0" size={16} />
                      <span className="text-gray-700 text-sm">{skill}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Philosophy */}
          <div className="bg-blue-50 border-l-4 border-blue-600 p-8 rounded-lg mb-8">
            <p className="text-gray-700 italic leading-relaxed text-center text-lg">
              "{directorInfo.philosophy}"
            </p>
          </div>

          {/* Achievements Section */}
          <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 border border-blue-100">
            <h4 className="text-2xl font-bold text-gray-900 mb-6 flex items-center justify-center gap-2">
              <Award className="text-blue-600" />
              Key Achievements & Contributions
            </h4>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {directorInfo.achievements.map((achievement, index) => (
                <div key={index} className="flex items-start gap-3 bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                  <div className="bg-blue-100 text-blue-600 rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm flex-shrink-0">
                    {index + 1}
                  </div>
                  <p className="text-gray-700 text-sm">{achievement}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* SMART Principles */}
        <div className="bg-gray-50 rounded-2xl p-8 md:p-12">
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our <span className="text-blue-600">SMART</span> Advertising Approach
            </h3>
            <p className="text-gray-600 text-lg">
              Every campaign we create follows the proven SMART framework for guaranteed success
            </p>
          </div>

          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-6">
            {smartPrinciples.items.map((item, index) => (
              <Card key={index} className="border-2 border-transparent hover:border-blue-600 transition-all hover:shadow-lg group">
                <CardContent className="p-6 text-center">
                  <div className="bg-blue-600 text-white w-16 h-16 rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-4 group-hover:scale-110 transition-transform">
                    {item.letter}
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">{item.word}</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Company Vision */}
        <div className="mt-16 text-center">
          <div className="max-w-4xl mx-auto">
            <img 
              src="https://customer-assets.emergentagent.com/job_insmart-media/artifacts/2rr0ek09_Gemini_Generated_Image_fhbqqofhbqqofhbq%20%281%29.png"
              alt="In Smart Media - Advertising is an Investment in Growth"
              width="800"
              height="256"
              loading="lazy"
              decoding="async"
              className="w-full h-64 object-cover rounded-2xl shadow-xl mb-8"
            />
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              Advertising is Not an Expense...
            </h3>
            <p className="text-2xl text-blue-600 font-semibold">
              It is an Investment in Growth
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
